import { Component, OnInit, ViewChild, inject, ChangeDetectionStrategy } from '@angular/core';
import { MatMenuTrigger, MatMenu, MatMenuContent, MatMenuItem } from '@angular/material/menu';
import { Router, RouterLink, ActivatedRoute } from '@angular/router';
import { WikiTree } from 'src/app/models/wiki-tree.model';
import { ApiService } from 'src/app/services/api.service';
import { WikiArticleUpdateDialogComponent } from './wiki-article/wiki-article-update-permissions-dialog/wiki-article-update-permissions-dialog.component';
import { WikiNodeCreateDialogComponent } from './wiki-node/wiki-node-create-dialog/wiki-node-create-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { MoveWikiItemDialogComponent } from 'src/app/components/utils/move-wiki-item-dialog/move-wiki-item-dialog.component';
import { WikiNode } from 'src/app/models/wiki-node.model';
import { PermissionsService } from 'src/app/services';
import { InputDialogComponent } from '../../shared/input-dialog/input-dialog.component';
import { MatIcon } from '@angular/material/icon';

@Component({
  selector: 'app-wiki',
  templateUrl: './wiki.component.html',
  styleUrls: ['./wiki.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [MatMenuTrigger, MatMenu, MatMenuContent, MatMenuItem, RouterLink, MatIcon],
})
export class WikiComponent implements OnInit {
  private api = inject(ApiService);
  protected router = inject(Router);
  private dialog = inject(MatDialog);
  private permissions = inject(PermissionsService);

  protected readonly page_title = inject(ActivatedRoute).snapshot.title;

  wikiTree: WikiTree[] = [];

  menuTopLeftPosition = { x: '0', y: '0' };
  // reference to the MatMenuTrigger in the DOM
  @ViewChild(MatMenuTrigger) contextMenu: MatMenuTrigger;

  ngOnInit(): void {
    this.fetchWikiTree();
  }

  createArticle() {
    const dialogRef = this.dialog.open(InputDialogComponent, {
      width: '400px',
      data: {
        title: 'Título de nuevo documento',
        description: `Introduce el título del nuevo documento.`,
        type: 'text',
        placeholder: 'Título de ejemplo...',
        label: 'Nuevo título',
        close_button_text: 'Crear',
      },
    });

    dialogRef.afterClosed().subscribe(titulo => {
      if (titulo) {
        const newWikiNodeName = {
          title: titulo,
          wiki_node_id: null,
        };
        this.api.createWikiArticle(newWikiNodeName).subscribe((data: any) => {
          this.fetchWikiTree();
        });
      }
    });
  }

  onRightClick(event: MouseEvent, item: any, type: string) {
    // preventDefault avoids to show the visualization of the right-click menu of the browser
    event.preventDefault();

    // we record the mouse position in our object
    this.menuTopLeftPosition.x = event.clientX + 'px';
    this.menuTopLeftPosition.y = event.clientY + 'px';

    // we open the menu
    // we pass to the menu the information about our object
    item.type = type;
    this.contextMenu.menuData = { item: item };

    // we open the menu
    this.contextMenu.openMenu();
  }

  showMoveWikiItemDialog() {
    const dialogRef = this.dialog.open(MoveWikiItemDialogComponent, {
      width: '500px',
    });
    dialogRef.afterClosed().subscribe(body => {
      this.moveWikiItem(body);
    });
  }

  moveWikiItem(wikiNode: WikiNode) {
    if (!this.contextMenu.menuData.item) return;
    if (this.contextMenu.menuData.item.type == 'WikiNode') {
      this.api
        .updateWikiNode(this.contextMenu.menuData.item.id, {
          wiki_node_id: wikiNode.id,
        })
        .subscribe((data: any) => {
          this.fetchWikiTree();
        });
    } else {
      this.api
        .updateWikiArticle(this.contextMenu.menuData.item.id, {
          wiki_node_id: wikiNode.id,
        })
        .subscribe((data: any) => {
          this.fetchWikiTree();
        });
    }
  }

  deleteNode() {
    if (!confirm('¿Estas seguro que quieres borrar esta carpeta? Se borraran todos los documentos que contenga.')) return;
    this.api.deleteWikiNode(this.contextMenu.menuData.item.id).subscribe((data: any) => {
      this.fetchWikiTree();
    });
  }

  editWikiNode(): void {
    const dialogRef = this.dialog.open(WikiNodeCreateDialogComponent, {
      width: 'auto',
      data: this.contextMenu.menuData.item,
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        const updatedData = {
          name: result.name,
          allowed_department_ids: result.categories && result.categories.length > 0 ? result.categories : null,
        };

        // Call an API or service method to update the folder with new data
        this.api.updateWikiNode(this.contextMenu.menuData.item.id, updatedData).subscribe(() => {
          this.fetchWikiTree(); // Refresh data after update
        });
      }
    });
  }

  deleteArticle() {
    if (!confirm('¿Estas seguro que quieres borrar este documento?')) return;
    this.api.deleteWikiArticle(this.contextMenu.menuData.item.id).subscribe((data: any) => {
      this.fetchWikiTree();
    });
  }

  editArticle() {
    const dialogRef = this.dialog.open(WikiArticleUpdateDialogComponent, {
      width: 'auto',
      data: this.contextMenu.menuData.item,
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        const updatedData = {
          allowed_department_ids: result.categories && result.categories.length > 0 ? result.categories : null,
        };
        this.api.updateWikiArticle(this.contextMenu.menuData.item.id, updatedData).subscribe((data: any) => {
          this.fetchWikiTree();
        });
      }
    });
  }

  isUserAdmin(): boolean {
    return this.permissions.isAdmin() ?? false;
  }

  private fetchWikiTree() {
    this.api.getWikiTree().subscribe(data => {
      this.wikiTree = data;
    });
  }
}
